import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  Crown, 
  Check, 
  Music, 
  Dumbbell, 
  BookOpen, 
  FileText, 
  Drum,
  Mic2,
  Sparkles,
  LogIn
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PremiumPage = () => {
  const { t, i18n } = useTranslation();
  const { user, isPremium, refreshUser, isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('lifetime');
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const status = searchParams.get('status');
    const sessionId = searchParams.get('session_id');
    if (!status) return;

    if (status === 'cancel') {
      toast.info(t('premium.checkout.cancelled'));
      setSearchParams({}, { replace: true });
      return;
    }

    if (status === 'success' && sessionId) {
      let cancelled = false;
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await axios.get(`${API}/payments/session/${sessionId}`);
          if (data.status === 'completed') {
            await refreshUser();
            toast.success(t('premium.checkout.activated'));
            setSearchParams({}, { replace: true });
            return;
          }
        } catch (err) {
          // ignore, retry
        }
        attempts += 1;
        if (!cancelled && attempts < 6) {
          setTimeout(poll, 1500);
        } else if (!cancelled) {
          toast.info(t('premium.checkout.processing'));
          setSearchParams({}, { replace: true });
        }
      };
      poll();
      return () => { cancelled = true; };
    }
  }, [searchParams, setSearchParams, refreshUser, t]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/payments/plans`);
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error(t('premium.checkout.errorPlans'));
    } finally {
      setLoading(false);
    }
  };

  const activatePremium = async () => {
    if (!isAuthenticated) {
      toast.error(t('premium.notLoggedIn'));
      navigate('/auth');
      return;
    }

    setActivating(true);
    try {
      const { data } = await axios.post(
        `${API}/payments/create-checkout-session`,
        null,
        { params: { plan_id: selectedPlan } }
      );
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error(t('premium.checkout.errorOpen'));
    } catch (error) {
      console.error('Error creating checkout session:', error);
      if (error.response?.status === 401) {
        toast.error(t('premium.checkout.errorAuth'));
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('premium.checkout.errorStart'));
      }
    } finally {
      setActivating(false);
    }
  };

  const features = [
    { icon: Music, label: t('premium.features.advancedChords') },
    { icon: Dumbbell, label: t('premium.features.expertExercises') },
    { icon: BookOpen, label: t('premium.features.fullTheory') },
    { icon: FileText, label: t('premium.features.allSongs') },
    { icon: Drum, label: t('premium.features.drumCustomization') },
    { icon: Mic2, label: t('premium.features.unlimitedRecordings') },
  ];

  const getPlanName = (plan) => i18n.language === 'it' ? plan.name_it : plan.name;

  const getPeriodLabel = (period) => t(`premium.periods.${period}`, { defaultValue: '' });

  // Already premium - show success state
  if (isPremium) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-6 md:px-12" data-testid="premium-page">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-amber/20 to-orange-500/20 border border-amber/30 rounded-3xl p-12"
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber to-orange-500 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
              <Crown className="w-10 h-10 text-black" />
            </div>
            <h1 className="font-heading text-4xl font-bold text-white mb-4">
              {t('premium.alreadyPremium')}
            </h1>
            <p className="text-zinc-400 text-lg mb-2">
              {t('premium.thanks')}
            </p>
            {user?.subscription_type && (
              <p className="text-amber font-medium">
                {t('premium.planLabel')}: {user.subscription_type.charAt(0).toUpperCase() + user.subscription_type.slice(1)}
              </p>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-6 md:px-12" data-testid="premium-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber to-orange-500 flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-black" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
            {t('premium.title')}
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {t('premium.subtitle')}
          </p>
        </motion.div>

        {/* Not logged in warning */}
        {!isAuthenticated && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue/10 border border-blue/30 rounded-2xl p-6 mb-8 text-center"
          >
            <LogIn className="w-8 h-8 text-blue mx-auto mb-3" />
            <p className="text-white font-medium mb-3">
              {t('premium.notLoggedIn')}
            </p>
            <Link to="/auth">
              <Button className="bg-blue hover:bg-blue/80 text-white">
                {t('premium.notLoggedInCta')}
              </Button>
            </Link>
          </motion.div>
        )}

        {/* Plans Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
          >
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.1 }}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative cursor-pointer rounded-2xl p-6 transition-all ${
                  selectedPlan === plan.id
                    ? 'bg-gradient-to-br from-amber/20 to-orange-500/20 border-2 border-amber'
                    : 'bg-[#121212] border border-white/10 hover:border-amber/50'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.discount && (
                  <div className="absolute -top-3 -right-3 bg-green-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                    -{plan.discount}
                  </div>
                )}
                
                {plan.id === 'lifetime' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    {t('premium.bestValue')}
                  </div>
                )}

                <div className="text-center mb-4">
                  <h3 className="font-heading text-xl font-bold text-white mb-2">
                    {getPlanName(plan)}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-heading text-4xl font-black text-amber">
                      €{plan.price}
                    </span>
                    <span className="text-zinc-400 text-sm">
                      {getPeriodLabel(plan.period)}
                    </span>
                  </div>
                </div>

                {/* Check indicator */}
                <div className={`w-6 h-6 rounded-full border-2 mx-auto flex items-center justify-center ${
                  selectedPlan === plan.id
                    ? 'bg-amber border-amber'
                    : 'border-zinc-600'
                }`}>
                  {selectedPlan === plan.id && (
                    <Check className="w-4 h-4 text-black" />
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Features Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#121212] border border-white/10 rounded-3xl p-8 mb-8"
        >
          <h3 className="font-heading text-xl font-bold text-white mb-6 text-center">
            {t('premium.features.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-white/5 rounded-xl"
              >
                <div className="w-10 h-10 rounded-lg bg-amber/20 flex items-center justify-center">
                  <feature.icon className="w-5 h-5 text-amber" />
                </div>
                <span className="text-white font-medium">{feature.label}</span>
                <Check className="w-5 h-5 text-green-400 ml-auto" />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Activate Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-md mx-auto"
        >
          <Button
            onClick={activatePremium}
            disabled={activating || !isAuthenticated}
            className="w-full h-14 bg-gradient-to-r from-amber to-orange-500 hover:from-amber/90 hover:to-orange-500/90 text-black font-bold text-lg rounded-xl disabled:opacity-50"
            data-testid="activate-premium-btn"
          >
            {activating ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                {t('premium.checkout.redirecting')}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5" />
                {t('premium.checkout.proceed')}
              </div>
            )}
          </Button>
          <p className="text-center text-zinc-500 text-sm mt-3">
            {t('premium.checkout.securedBy')}
          </p>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center mt-8"
        >
          <div className="flex flex-wrap justify-center gap-6 text-zinc-500 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              {t('premium.trust.instantAccess')}
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              {t('premium.trust.allContent')}
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              {t('premium.trust.futureUpdates')}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PremiumPage;
